import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import { FadeLoader } from "react-spinners";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles"; // Keeping makeStyles as in your original code
import httpRequest from "../../httpRequest";

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
  },
  loginForm: {
    background: "radial-gradient(circle, #ffffff 50%,#f0f0f0 100%)",
    border: "3px solid #fff",
    borderRadius: "20px",
    boxShadow: "0 -8px 32px 0 rgba(31, 38, 135, 0.1), 0 8px 32px 0 rgba(31, 38, 135, 0.1)", // Added top shadow
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },
  gradientButton: {
    background: "linear-gradient(to right, #2492be, #758b72, #e1833f) !important",
    color: "white !important",
    borderRadius: "12px !important",
    padding: "10px 0 !important",
    textTransform: "none !important",
    fontFamily: "SapFiori72 !important",
    boxShadow: "0 0 8px rgba(36, 146, 190, 0.6), 0 0 16px rgba(225, 131, 63, 0.4) !important",
    transition: "all 0.3s ease-in-out !important",
    "&:hover": {
      boxShadow: "0 0 12px rgba(36, 146, 190, 0.8), 0 0 20px rgba(225, 131, 63, 0.6), 0 0 24px rgba(117, 139, 114, 0.5) !important",
      transform: "scale(1.05) !important",
    },
  },
  iframeContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    border: "none",
  },
}));

const LoginPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const submitHandler = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const username = data.get("username");
    const password = data.get("password");
    setLoading(true);

    try {
      // Assuming httpRequest is defined and configured elsewhere
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/login`, {
        email: username,
        password,
      });
      // Assuming successful login redirects
      // Use navigate or window.location.href based on your routing needs
      window.location.href = "/";
    } catch (error) {
      setLoading(false);
      if (error.response && error.response.status === 401) {
        enqueueSnackbar("Wrong username or password", { variant: "error" });
      } else {
        // Log the error for debugging purposes
        console.error("Login server error:", error);
        enqueueSnackbar("Server error. Please try again later.", { variant: "error" });
      }
    }
  };

  useEffect(() => {
    // Disable right-click if intentional
    const handleContextMenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Grid container sx={{ flex: 1 }}>
        {/* Left Grid with iframe */}
        <Grid
          item
          xs={0} // Hide on xs (small screens)
          md={7} // Takes 7/12 width on md (medium) and up
          lg={8} // Takes 8/12 width on lg (large) and up
          sx={{
            position: "relative",
            display: { xs: "none", md: "flex" }, // Hide on xs, show on md+
            justifyContent: "center",
            alignItems: "center",
            background: "linear-gradient(to bottom, #e0f7fa, #b0bec5)",
            overflow: "hidden", // Ensure iframe stays within bounds
          }}
          onContextMenu={(e) => e.preventDefault()} // Disable right-click on this grid item
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
            }}
          >
            <iframe
              src="/Welcome/frame.html"
              title="Welcome"
              style={{
                border: "none",
                width: "100%",
                height: "100%",
              }}
            />
          </Box>
        </Grid>

        {/* Right Grid with Login Form */}
        <Grid
          item
          xs={12} // Takes full width on xs
          md={5} // Takes 5/12 width on md and up
          lg={4} // Takes 4/12 width on lg and up
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#fff",
            padding: 2, // Add some general padding to the grid item content
            overflowY: "auto",
            px: { xs: 2, md: 4 },
          }}
        >
          {/* Responsive Logo Styling with slightly less bottom margin on small screens */}
          <Box
            component="img"
            src="/logo.gif"
            alt="SmartIDP Logo"
            sx={{
              width: "100%", // Logo takes full width of its container (up to max)
              maxWidth: "450px", // But doesn't get larger than 450px
              height: "auto",   // Maintain aspect ratio
              mb: { xs: 2, md: 2 },
            }}
          />

          {/* Login Form Box - Apply responsive width/margin and padding here */}
          <Box
            className={classes.loginForm} // Keep styles from makeStyles (background, border, shadow, etc.)
            sx={{ // Add responsive layout styles using sx
                width: "100%",       // Take full width of its container
                maxWidth: "450px",   // But don't exceed 450px
                mx: 'auto',          // Center horizontally when less than maxWidth

                // Apply responsive padding inside the form box - reduced values
                padding: { xs: 2, sm: 3, md: 3 }, // Adjusted padding values
              }}
          >
            <Typography
              variant="h4"
              gutterBottom // Adds default bottom margin to Typography
              sx={{
                fontFamily: "SapFiori72",
                fontWeight: "bold",
                color: "#354a5f",
                textAlign: "center",
              }}
            >
              Login
            </Typography>
            {/* Use Box for the separator - reduced vertical margin */}
            <Box sx={{ borderBottom: '1px solid #e0e0e0', my: { xs: 1.5, md: 2 } }} />


            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "150px", // Fixed height for loader space
                }}
              >
                <FadeLoader color="#354a5f" size={50} />
              </Box>
            ) : (
              <Box component="form" onSubmit={submitHandler} sx={{ width: "100%" }}> {/* Ensure form takes full width of its container */}
                <TextField
                  margin="dense" // <<< Changed from "normal" to "dense"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="off"
                  autoFocus
                   // Keep your InputProps and InputLabelProps styles
                  InputProps={{
                    style: {
                      borderRadius: "10px",
                      backgroundColor: "#f3f4f6",
                      fontFamily: "SapFiori72",
                    },
                  }}
                  InputLabelProps={{
                    style: { color: "#757575", fontFamily: "SapFiori72" },
                  }}
                  sx={{ // Keep your focus/hover border styles
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#e0e0e0" },
                      "&:hover fieldset": { borderColor: "#b0bec5" },
                      "&.Mui-focused fieldset": { borderColor: "#00c4cc" },
                    },
                    // Optional: Adjust margin bottom more explicitly if needed
                    // mb: { xs: 1, md: 2 }
                  }}
                />
                <TextField
                  margin="dense" // <<< Changed from "normal" to "dense"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="off"
                   // Keep your InputProps and InputLabelProps styles
                  InputProps={{
                    style: {
                      borderRadius: "10px",
                      backgroundColor: "#f3f4f6",
                      fontFamily: "SapFiori72",
                    },
                  }}
                  InputLabelProps={{
                    style: { color: "#757575", fontFamily: "SapFiori72" },
                  }}
                  sx={{ // Keep your focus/hover border styles
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#e0e0e0" },
                      "&:hover fieldset": { borderColor: "#b0bec5" },
                      "&.Mui-focused fieldset": { borderColor: "#00c4cc" },
                    },
                    // Optional: Adjust margin bottom more explicitly if needed
                    // mb: { xs: 1, md: 2 }
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className={classes.gradientButton} // Using makeStyles class for specific button styles
                  sx={{
                    mt: { xs: 2.5, md: 3 }, // Slightly reduced margin-top on extra-small screens
                    // Optional: Make button margin responsive if needed
                    // mt: { xs: 2, md: 3 }
                  }}
                >
                  Login
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoginPage;